/**
 * Evidence Board Component
 *
 * Main React Flow canvas for the Evidence Graph
 * Renders LogicalChainGraph with custom node types and ELK layout
 * Supports hierarchical node structure with expand/collapse and native grouping
 */

import React, { useMemo, useCallback, useState, useRef, useEffect, createContext } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  Position,
  useNodesState,
  useEdgesState,
  NodeChange,
  ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';

import {
  EvidenceNode,
  ProcessingNode,
  ExpandableNode,
  GroupNode,
  ChildNode,
  GraphNode,
  GRAPH_NODE_HEIGHT,
  GRAPH_NODE_WIDTH,
  ExpandableNodeData,
  GraphNodeData,
} from './nodes';
import ResearchEdgeComponent from './ResearchEdge';
import type { ResearchEdgeData } from './ResearchEdge';
import GraphLegend from './GraphLegend';
import {
  LogicalChainGraph,
  LogicalNode,
  WorkspaceNodeData,
  NodeType,
  getNodeTypeColor,
  isGroupNode,
  ProcessNode,
  ProcessEdge,
  isProcessGraphFormat,
} from './types';
import {
  calculateElkLayout,
  getAllEdges,
  flattenAllNodes,
  calculateElkLayoutFlat,
  getAllEdgesFromFlat,
} from './elkLayout';
import { DictWorkspaceState } from '../../types/workspaceState';
import type { Cluster } from '../../types/cluster';
import { ClusterStatus, CLUSTER_STATUS_CSS_VARS, CLUSTER_VOCAB } from '../../types/cluster';
import { isDictWorkspaceFormat, dictWorkspaceToGraph } from '../../utils/workspaceConversions';
import { HighlightContext, EMPTY_HIGHLIGHT } from '../shared/HighlightContext';
import type { HighlightState } from '../shared/HighlightContext';
import { SelectedClusterContext } from '../shared/SelectedClusterContext';
import { clusterHighlight } from '../shared/clusterHighlight';
import type { HighlightEdge } from '../shared/clusterHighlight';
import { HoverContext } from '../shared/HoverContext';
import ClusterRegionNode from '../cluster/ClusterRegionNode';
import StepNumberNode from '../cluster/StepNumberNode';
import { buildClusteredElkGraph, GRAPH_LAYER, HEADER_LAYER_HEIGHT, UNSORTED_CLUSTER_NODE_ID } from '../cluster/clusterLayout';
import { ClusterHeaderRectsContext } from '../cluster/ClusterHeaderRectsContext';
import type { HeaderRect } from '../cluster/ClusterHeaderRectsContext';
import GraphCanvas from '../cluster/GraphCanvas';
import type { CanvasLegendEntry } from '../cluster/GraphCanvas';
import { useProjectStore } from '../../stores/projectStore';

function statusToLegendEntry(status: ClusterStatus): CanvasLegendEntry {
  const statusVar = CLUSTER_STATUS_CSS_VARS[status];
  const isApproved = status === ClusterStatus.APPROVED;
  return {
    label: status.replace(/_/g, ' '),
    color: isApproved
      ? `var(${statusVar})`
      : `color-mix(in srgb, var(${statusVar}) 30%, transparent)`,
    ringColor: `var(${statusVar})`,
    labelClassName: 'capitalize',
  };
}

// Context so GraphNode can read the open node ID without going through node data.
// Avoids the fragile setNodes-effect chain that kept getting overwritten by syncs.
export const OpenNodeIdContext = createContext<string | null>(null);

// =============================================================================
// NODE TYPES REGISTRY (must be defined at module level for React Flow)
// =============================================================================

/**
 * Custom node types for React Flow
 * IMPORTANT: This must be defined outside the component to prevent
 * React Flow from recreating node instances on every render
 */
const nodeTypes = {
  evidence: EvidenceNode,
  processing: ProcessingNode,
  expandable: ExpandableNode,
  group: GroupNode,
  child: ChildNode,
  graphNode: GraphNode, // New minimal node for flat format
  'cluster-region': ClusterRegionNode,
  // Research splits the cluster box like build: a 'cluster-region' BODY node
  // (zebra, below the edges) + this 'cluster-region-header' child (title bar,
  // above the edges) so connector lines run between them. ClusterRegionNode
  // renders the right slice off its `layer` data field.
  'cluster-region-header': ClusterRegionNode,
  // Per-tier gutter numerals (Q, 1, 2, 3…). Build registers this too; research
  // didn't until tier numbering landed — without it React Flow falls back to a
  // default ~150px white box that overlaps each cluster.
  'step-number': StepNumberNode,
};

const edgeTypes = {
  researchEdge: ResearchEdgeComponent,
};

// =============================================================================
// HIERARCHY HELPERS
// =============================================================================

/**
 * Count total children recursively
 */
function countChildren(node: LogicalNode): number {
  if (!node.children) return 0;
  return node.children.reduce((sum, child) => sum + 1 + countChildren(child), 0);
}

/**
 * Simple synchronous layout fallback
 * Used when ELK layout is pending or fails
 */
function simpleLayout(
  nodes: LogicalNode[],
  edges: { source_node_id: string; target_node_id: string }[]
): Record<string, { x: number; y: number }> {
  const positions: Record<string, { x: number; y: number }> = {};
  const SPACING_X = 300;
  const SPACING_Y = 180;

  // Flatten all nodes
  const flatNodes = flattenAllNodes(nodes);

  // Group by depth
  const byDepth: Record<number, typeof flatNodes> = {};
  flatNodes.forEach((fn) => {
    if (!byDepth[fn.depth]) byDepth[fn.depth] = [];
    byDepth[fn.depth].push(fn);
  });

  // Position each level
  Object.entries(byDepth).forEach(([depthStr, nodesAtDepth]) => {
    const depth = parseInt(depthStr);
    nodesAtDepth.forEach((fn, idx) => {
      positions[fn.node.node_id] = {
        x: idx * SPACING_X,
        y: depth * SPACING_Y,
      };
    });
  });

  return positions;
}

/**
 * Simple layout fallback for flat format (ProcessNodes with parent_id)
 * Uses edge-based layer calculation for waterfall (top-to-bottom) flow
 */
function simpleLayoutFlat(
  nodes: ProcessNode[],
  edges: ProcessEdge[]
): Record<string, { x: number; y: number }> {
  const positions: Record<string, { x: number; y: number }> = {};
  const SPACING_X = 150;  // Horizontal spacing within layer
  const SPACING_Y = 120;  // Vertical spacing between layers

  // Build adjacency for layer calculation
  const outgoing = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  nodes.forEach(n => {
    outgoing.set(n.node_id, []);
    inDegree.set(n.node_id, 0);
  });

  edges.forEach(e => {
    outgoing.get(e.source_node_id)?.push(e.target_node_id);
    inDegree.set(e.target_node_id, (inDegree.get(e.target_node_id) || 0) + 1);
  });

  // BFS from roots to assign layers
  const layerMap = new Map<string, number>();
  const queue: string[] = [];

  nodes.forEach(n => {
    if ((inDegree.get(n.node_id) || 0) === 0) {
      queue.push(n.node_id);
      layerMap.set(n.node_id, 0);
    }
  });

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const layer = layerMap.get(nodeId) || 0;

    for (const targetId of outgoing.get(nodeId) || []) {
      const currentLayer = layerMap.get(targetId);
      if (currentLayer === undefined || currentLayer < layer + 1) {
        layerMap.set(targetId, layer + 1);
      }
      const newInDegree = (inDegree.get(targetId) || 1) - 1;
      inDegree.set(targetId, newInDegree);
      if (newInDegree === 0) {
        queue.push(targetId);
      }
    }
  }

  // Handle disconnected nodes
  nodes.forEach(n => {
    if (!layerMap.has(n.node_id)) {
      layerMap.set(n.node_id, 0);
    }
  });

  // Group by layer
  const byLayer: Record<number, ProcessNode[]> = {};
  nodes.forEach(node => {
    const layer = layerMap.get(node.node_id) || 0;
    if (!byLayer[layer]) byLayer[layer] = [];
    byLayer[layer].push(node);
  });

  // Position each layer - VERTICAL (waterfall) flow
  Object.entries(byLayer).forEach(([layerStr, nodesInLayer]) => {
    const layer = parseInt(layerStr);
    const totalWidth = (nodesInLayer.length - 1) * SPACING_X;
    const startX = -totalWidth / 2;

    nodesInLayer.forEach((node, idx) => {
      positions[node.node_id] = {
        x: startX + idx * SPACING_X,
        y: layer * SPACING_Y,
      };
    });
  });

  return positions;
}

function firstNonEmptyLine(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  return value.split(/\r?\n/).map((line) => line.trim()).find(Boolean);
}

function researchMissionSubtitle(mission: unknown): string | undefined {
  if (!mission) {
    return undefined;
  }
  const directText = firstNonEmptyLine(mission);
  if (directText) {
    return directText;
  }
  if (typeof mission === 'object') {
    return firstNonEmptyLine((mission as { query?: unknown }).query);
  }
  return undefined;
}

// =============================================================================
// PROPS
// =============================================================================

interface EvidenceBoardProps {
  /** Graph data - can be LogicalChainGraph (arrays) or DictWorkspaceState (dicts) */
  graph: LogicalChainGraph | DictWorkspaceState;
  clusters?: Record<string, Cluster>;
  enableClusters?: boolean;
  onNodeSelect?: (nodeId: string | null) => void;
  /** Callback for double-clicking a node (e.g., to open its workspace) */
  onNodeDoubleClick?: (nodeId: string) => void;
  // Note: Selection highlight is handled by React Flow's native 'selected' prop
  // No need for selectedNodeId - it caused position recalculation on every click
  editable?: boolean;
  onPositionsChange?: (positions: Record<string, { x: number; y: number }>) => void;
  initialPositions?: Record<string, { x: number; y: number }>;
  onExpandedChange?: (expandedNodeIds: string[]) => void;
  initialExpandedNodes?: string[];
  onFindingClick?: (findingId: string) => void;
  /** Node ID to highlight (from reference click in chat). Triggers pulse animation and fitView. */
  highlightedNodeId?: string | null;
  /** Trigger re-layout when this value changes (e.g., on research completion) */
  triggerReLayout?: number;
  /** Node ID whose detail panel is currently open — drives persistent open-state highlight */
  openNodeId?: string | null;
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function EvidenceBoard({
  graph: rawGraph,
  clusters,
  enableClusters = true,
  onNodeSelect,
  onNodeDoubleClick,
  editable = false,
  onPositionsChange,
  initialPositions,
  onExpandedChange,
  initialExpandedNodes = [],
  onFindingClick,
  highlightedNodeId,
  triggerReLayout,
  openNodeId,
}: EvidenceBoardProps) {
  // ==========================================================================
  // FORMAT DETECTION AND CONVERSION
  // Convert dict-based workspace to array-based LogicalChainGraph for React Flow
  // ==========================================================================

  const graph = useMemo(() => {
    if (!rawGraph) {
      return { nodes: [], edges: [] } as LogicalChainGraph;
    }

    // Check if dict format (from Report Library /workspaces/{id}/state)
    if (isDictWorkspaceFormat(rawGraph)) {
      console.log('[EvidenceBoard] Converting dict workspace to graph:', {
        nodes: Object.keys((rawGraph as DictWorkspaceState).nodes).length,
        edges: Object.keys((rawGraph as DictWorkspaceState).edges).length,
      });
      // Convert and cast to local LogicalChainGraph type
      const converted = dictWorkspaceToGraph(rawGraph as DictWorkspaceState);
      return converted as unknown as LogicalChainGraph;
    }

    // Already in array format
    return rawGraph as LogicalChainGraph;
  }, [rawGraph]);

  const clustersForGraph = useMemo<Record<string, Cluster>>(() => {
    if (clusters) {
      return clusters;
    }
    if (rawGraph && isDictWorkspaceFormat(rawGraph)) {
      return (rawGraph as DictWorkspaceState).clusters || {};
    }
    return {};
  }, [clusters, rawGraph]);

  const activeProject = useProjectStore((state) => state.activeProject);
  const graphTitle = activeProject?.name ?? 'Research';
  const graphSubtitle = useMemo(
    () => researchMissionSubtitle(activeProject?.research_mission),
    [activeProject?.research_mission],
  );
  const statusLegend = useMemo<CanvasLegendEntry[]>(
    () => Array.from(new Set(Object.values(clustersForGraph).map((cluster) => cluster.status)))
      .map(statusToLegendEntry),
    [clustersForGraph],
  );
  const [showEdgeLabels, setShowEdgeLabels] = useState(false);

  // Track selected node internally to preserve selection when nodes sync
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // Clicking a cluster region highlights its members + everything they connect
  // to. Mutually exclusive with node selection (each clears the other).
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  // Hovered node id — pushed into HoverContext so ResearchEdge can decide
  // whether its label should light up (fixes label overlap between
  // sibling edges that share a decision-point source).
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  // Path highlighting is computed as highlightState (useMemo) and provided via HighlightContext.
  // GraphNode and ResearchEdge read it from context — immune to setNodes overwrites.

  // Track expanded nodes for hierarchical display
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    new Set(initialExpandedNodes)
  );

  // Use ref for expand callback to avoid re-renders during useMemo
  const onExpandedChangeRef = useRef(onExpandedChange);
  onExpandedChangeRef.current = onExpandedChange;

  // Handle expand/collapse toggle - stable reference using ref
  const handleToggleExpand = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      // Notify parent of change (use ref to avoid dependency)
      onExpandedChangeRef.current?.(Array.from(next));
      return next;
    });
  }, []); // No dependencies - uses ref internally

  // State for ELK layout results
  const [elkLayout, setElkLayout] = useState<{
    positions: Record<string, { x: number; y: number }>;
    dimensions: Record<string, { width: number; height: number }>;
    parentMap: Record<string, string | undefined>;
  } | null>(null);

  // Detect if graph is in flat format (parent_id) or nested format (children)
  const isFlatFormat = useMemo(() => {
    return graph ? isProcessGraphFormat(graph) : false;
  }, [graph]);

  /**
   * Compute the path from root (question) to a target node
   * Uses backward traversal from target to find all ancestors
   */
  const computePathToNode = useCallback((targetNodeId: string): {
    nodeIds: Set<string>;
    edgeKeys: Set<string>;
  } => {
    if (!graph?.nodes || !graph?.edges) {
      return { nodeIds: new Set(), edgeKeys: new Set() };
    }

    const nodeIds = new Set<string>();
    const edgeKeys = new Set<string>();

    // Build incoming edges map for backward traversal
    const incomingEdges = new Map<string, string[]>();

    if (isFlatFormat) {
      const processEdges = (graph.edges || []) as unknown as ProcessEdge[];
      processEdges.forEach(e => {
        if (!incomingEdges.has(e.target_node_id)) {
          incomingEdges.set(e.target_node_id, []);
        }
        incomingEdges.get(e.target_node_id)!.push(e.source_node_id);
      });
    } else {
      (graph.edges || []).forEach((e: { source_node_id: string; target_node_id: string }) => {
        if (!incomingEdges.has(e.target_node_id)) {
          incomingEdges.set(e.target_node_id, []);
        }
        incomingEdges.get(e.target_node_id)!.push(e.source_node_id);
      });
    }

    // BFS backward from target to find all paths to root
    const queue = [targetNodeId];
    nodeIds.add(targetNodeId);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const parents = incomingEdges.get(current) || [];

      for (const parent of parents) {
        edgeKeys.add(`${parent}-${current}`);
        if (!nodeIds.has(parent)) {
          nodeIds.add(parent);
          queue.push(parent);
        }
      }
    }

    return { nodeIds, edgeKeys };
  }, [graph, isFlatFormat]);


  // Auto-expand only the root node by default (all other groups start collapsed)
  // Root node is the one with parent_id === null and node_type === 'group'
  useEffect(() => {
    if (isFlatFormat && graph?.nodes && initialExpandedNodes.length === 0) {
      const processNodes = graph.nodes as unknown as ProcessNode[];
      const rootGroupNode = processNodes.find(n => n.node_type === 'group' && n.parent_id === null);

      if (rootGroupNode) {
        setExpandedNodes(new Set([rootGroupNode.node_id]));
        onExpandedChangeRef.current?.([rootGroupNode.node_id]);
      }
    }
  }, [isFlatFormat, graph?.nodes, initialExpandedNodes.length]);

  // Calculate ELK layout asynchronously
  useEffect(() => {
    if (!graph || !graph.nodes || graph.nodes.length === 0) {
      setElkLayout(null);
      return;
    }

    // Skip if user provided positions
    if (initialPositions && Object.keys(initialPositions).length > 0) {
      return;
    }

    // Use appropriate layout function based on format
    if (isFlatFormat) {
      // Flat format: nodes have parent_id
      calculateElkLayoutFlat(
        graph.nodes as unknown as ProcessNode[],
        (graph.edges || []) as unknown as ProcessEdge[]
      ).then(setElkLayout);
    } else {
      // Nested format: nodes have children arrays
      calculateElkLayout(graph.nodes, graph.edges).then(setElkLayout);
    }
  }, [graph, initialPositions, isFlatFormat]);

  // Convert LogicalChainGraph to React Flow format
  const { initialNodes, initialEdges } = useMemo(() => {
    if (!graph || !graph.nodes || graph.nodes.length === 0) {
      return { initialNodes: [], initialEdges: [] };
    }

    // Use provided positions, ELK layout, or format-appropriate fallback
    let positions: Record<string, { x: number; y: number }>;
    if (initialPositions && Object.keys(initialPositions).length > 0) {
      positions = initialPositions;
    } else if (elkLayout?.positions) {
      positions = elkLayout.positions;
    } else if (isFlatFormat) {
      // Flat format fallback
      positions = simpleLayoutFlat(
        graph.nodes as unknown as ProcessNode[],
        (graph.edges || []) as unknown as ProcessEdge[]
      );
    } else {
      // Nested format fallback
      positions = simpleLayout(graph.nodes, graph.edges);
    }

    let nodes: Node<WorkspaceNodeData | ExpandableNodeData | GraphNodeData>[];
    let allEdges: { source: string; target: string; isInternal: boolean }[];

    if (isFlatFormat) {
      // =================================================================
      // FLAT FORMAT: nodes have parent_id references
      // Supports collapse/expand for GROUP nodes
      // =================================================================
      const processNodes = graph.nodes as unknown as ProcessNode[];
      const processEdges = (graph.edges || []) as unknown as ProcessEdge[];

      // Identify root and terminal nodes for visual styling
      const nodeIdsWithOutgoing = new Set(processEdges.map(e => e.source_node_id));
      const nodeIdsWithIncoming = new Set(processEdges.map(e => e.target_node_id));

      // Build parent-child map for visibility calculation
      const nodeById = new Map(processNodes.map(n => [n.node_id, n]));

      // Determine which nodes should be hidden based on collapsed ancestors
      const isNodeHidden = (nodeId: string): boolean => {
        const node = nodeById.get(nodeId);
        if (!node || !node.parent_id) return false;

        // Check if parent is a collapsed group
        const parent = nodeById.get(node.parent_id);
        if (parent && parent.node_type === 'group' && !expandedNodes.has(parent.node_id)) {
          return true;
        }

        // Check ancestors recursively
        return isNodeHidden(node.parent_id);
      };

      // Debug: log node count and positions
      console.log(`[EvidenceBoard] Flat format: ${processNodes.length} nodes, ${processEdges.length} edges`);
      console.log('[EvidenceBoard] Expanded nodes:', Array.from(expandedNodes));

      // Create React Flow nodes using minimal GraphNode design
      nodes = processNodes.map((node) => {
        const isGroup = node.node_type === 'group';

        // Get position from layout
        const position = positions[node.node_id] || { x: 0, y: 0 };

        // Count direct children for group nodes
        const childCount = processNodes.filter(n => n.parent_id === node.node_id).length;

        // Determine if root (no incoming edges) or terminal (no outgoing edges)
        const isRoot = !nodeIdsWithIncoming.has(node.node_id);
        const isTerminal = !nodeIdsWithOutgoing.has(node.node_id);

        // Determine if this node should be hidden (parent is collapsed)
        const hidden = isNodeHidden(node.node_id);

        return {
          id: node.node_id,
          type: 'graphNode',
          position,
          hidden,
          data: {
            nodeType: node.node_type as NodeType,
            label: node.label,
            isRoot,
            isTerminal,
            childCount: isGroup ? childCount : undefined,
            // Expand/collapse for group nodes
            isExpanded: expandedNodes.has(node.node_id),
            onToggleExpand: handleToggleExpand,
            nodeId: node.node_id,
            // Plan graph: workspace type for label rendering
            workspaceType: (node as any).workspace_type,
            deliverable: (node as any).deliverable,
          } as GraphNodeData,
          sourcePosition: Position.Bottom,  // Waterfall flow: top-to-bottom
          targetPosition: Position.Top,
        };
      });

      // Get all edges from flat format
      allEdges = getAllEdgesFromFlat(processNodes, processEdges);

    } else {
      // =================================================================
      // NESTED FORMAT: nodes have children arrays (legacy)
      // =================================================================
      const flattenedNodes = flattenAllNodes(graph.nodes);

      // Build a map for quick lookup
      const nodeMap: Record<string, LogicalNode> = {};
      flattenedNodes.forEach((fn) => {
        nodeMap[fn.node.node_id] = fn.node;
      });

      // Create React Flow nodes using icon-based GraphNode design
      nodes = flattenedNodes.map(({ node, parentId, depth }) => {
        const hasChildren = isGroupNode(node);

        // Calculate position
        const position = positions[node.node_id] || { x: 0, y: 0 };

        // Only show child nodes if parent is expanded
        const isHidden = parentId ? !expandedNodes.has(parentId) : false;

        // Determine if root (first node / question) or terminal (leaf nodes)
        const isRoot = depth === 0;
        const isTerminal = !hasChildren && !node.children?.length;

        return {
          id: node.node_id,
          type: 'graphNode',  // Always use icon-based design
          position,
          hidden: isHidden,
          data: {
            nodeType: node.node_type as NodeType,
            label: node.label,
            isRoot,
            isTerminal,
            childCount: hasChildren ? countChildren(node) : undefined,
            // Expand/collapse for group nodes
            isExpanded: expandedNodes.has(node.node_id),
            onToggleExpand: handleToggleExpand,
            nodeId: node.node_id,
            // Plan graph: workspace type for label rendering
            workspaceType: (node as any).workspace_type,
            deliverable: (node as any).deliverable,
          } as GraphNodeData,
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
        };
      });

      // Get all edges including internal child edges
      allEdges = getAllEdges(graph.nodes, graph.edges);
    }

    // Create React Flow edges - only show edges where both nodes are visible
    // Build node lookup for deriving edge labels from source node deliverable
    const nodeDataMap = new Map<string, GraphNodeData>();
    nodes.forEach(n => nodeDataMap.set(n.id, n.data as GraphNodeData));

    const visibleNodeIds = new Set(nodes.filter(n => !n.hidden).map(n => n.id));
    const edges: Edge<ResearchEdgeData>[] = allEdges
      .filter(edge => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target))
      .map((edge) => {
        const isHierarchy = 'isHierarchy' in edge && edge.isHierarchy;
        const edgeType = 'edge_type' in edge ? (edge as any).edge_type : undefined;
        const isConditional = edgeType === 'conditional';

        // Edge label: prefer source node's deliverable, fall back to edge metadata
        const sourceNodeData = nodeDataMap.get(edge.source);
        const metadataLabel = 'label' in edge ? (edge as any).label : undefined;
        const edgeLabel = sourceNodeData?.deliverable || metadataLabel;

        return {
          id: `${edge.source}-${edge.target}`,
          source: edge.source,
          target: edge.target,
          type: 'researchEdge',
          data: {
            label: edgeLabel,
            isInternal: edge.isInternal,
            isHierarchy: !!isHierarchy,
            isConditional,
          } as ResearchEdgeData,
        };
      });

    return { initialNodes: nodes, initialEdges: edges };
    // Note: selectedNodeId is intentionally NOT a dependency here
    // Selection highlight is handled by React Flow's native 'selected' prop
    // Adding selectedNodeId would cause full recalculation on every selection change,
    // which resets all node positions and causes scatter on drag
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, expandedNodes, initialPositions, elkLayout, handleToggleExpand, isFlatFormat]);
  // Note: onFindingClick intentionally excluded — it's not used in this memo body,
  // and including it caused initialNodes to recompute on every click (new function ref
  // from parent re-render), which reset selection/open state via the sync effect.

  const nodeClusterById = useMemo(() => {
    const mapping = new Map<string, string | null>();
    if (!graph?.nodes) {
      return mapping;
    }

    if (isFlatFormat) {
      (graph.nodes as unknown as ProcessNode[]).forEach((node) => {
        mapping.set(node.node_id, node.cluster_id ?? null);
      });
      return mapping;
    }

    flattenAllNodes(graph.nodes as LogicalNode[]).forEach(({ node }) => {
      mapping.set(node.node_id, node.cluster_id ?? null);
    });
    return mapping;
  }, [graph, isFlatFormat]);

  const [clusterLayoutVersion, setClusterLayoutVersion] = useState(0);
  const [clusteredLayout, setClusteredLayout] = useState<{
    rfNodes: Node[];
    rfEdges: Edge[];
  } | null>(null);

  const clusterSignature = useMemo(
    () => JSON.stringify({
      clusters: Object.values(clustersForGraph).map((cluster) => ({
        id: cluster.cluster_id,
        name: cluster.name,
        status: cluster.status,
        parent: cluster.parent_cluster_id,
        members: cluster.member_node_ids,
        summary: cluster.summary,
      })),
      nodeClusters: Array.from(nodeClusterById.entries()),
      nodes: initialNodes.map((node) => `${node.id}:${node.hidden ? 'hidden' : 'visible'}`),
      edges: initialEdges.map((edge) => `${edge.id}:${edge.source}->${edge.target}`),
      version: clusterLayoutVersion,
    }),
    [clustersForGraph, nodeClusterById, initialNodes, initialEdges, clusterLayoutVersion],
  );

  useEffect(() => {
    let cancelled = false;

    if (!enableClusters || initialNodes.length === 0) {
      setClusteredLayout(null);
      return;
    }

    async function buildLayout() {
      const rawNodes = initialNodes.map((node) => ({
        id: node.id,
        type: node.type,
        // Compact node footprint. Width/height are the GraphNode's fixed card
        // dimensions (single source of truth) so the ELK slot and the rendered
        // card agree exactly — no drift.
        width: GRAPH_NODE_WIDTH,
        height: GRAPH_NODE_HEIGHT,
        cluster_id: nodeClusterById.get(node.id) ?? null,
        data: node.data,
        selected: node.selected,
        hidden: node.hidden,
        className: node.className,
        sourcePosition: node.sourcePosition,
        targetPosition: node.targetPosition,
        style: node.style,
      }));

      const rawEdges = initialEdges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.type,
        data: edge.data,
        animated: edge.animated,
        hidden: edge.hidden,
        className: edge.className,
        style: edge.style,
      }));

      const nextLayout = await buildClusteredElkGraph(
        rawNodes,
        rawEdges,
        Object.values(clustersForGraph),
        {
          vocab: CLUSTER_VOCAB.research,
          // Keep research's dependency tiers (multi-column rows by depth), but
          // adopt build's left-alignment + numbering: each tier is flush-left
          // and carries one gutter numeral (1, 2, 3…) shared by every cluster
          // in it; the framing question gets a "Q" above tier 1.
          interiorLayout: 'tiered',
          interClusterLayout: 'tiered',
          outerAlignment: 'left',
          stepNumbers: true,
          nodeNodeSpacing: 20,
          clusterLabelHeight: 28,
          padding: { top: 28, right: 12, bottom: 12, left: 12 },
        },
      );

      if (!cancelled) {
        setClusteredLayout(nextLayout);
      }
    }

    buildLayout();
    return () => { cancelled = true; };
    // Gate the ELK rebuild on clusterSignature (structure only) — NOT on the raw
    // initialNodes/initialEdges refs. Those get a new reference whenever a drag
    // saves node_positions, which would re-run ELK and snap dragged clusters back
    // to the computed layout. clusterSignature deliberately excludes positions, and
    // buildClusteredElkGraph recomputes positions itself, so a stale-but-structurally
    // -identical closure is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterSignature, enableClusters]);

  // Restore manually-dragged cluster-region positions. ELK always recomputes a
  // fresh region layout; a saved absolute position (from a prior drag, persisted
  // in workspace_ui_state) wins so clusters stay where the user put them across
  // reloads. Children use parent-relative positions, so overriding the region
  // alone repositions the whole cluster. Applied here (not inside the ELK effect)
  // so late-arriving initialPositions still restore without re-running layout.
  const renderNodes = useMemo(() => {
    const base = clusteredLayout?.rfNodes ?? initialNodes;
    if (!initialPositions || Object.keys(initialPositions).length === 0) {
      return base;
    }
    return base.map((node) => {
      // Restore saved drags for the cluster boxes AND the framing question (a
      // parentless top-level node). Cluster member nodes are parent-relative
      // children — they must NOT be restored from absolute saved positions
      // (they ride their parent). `!parentId` selects exactly box + question,
      // which is what makes the question stay put instead of snapping back.
      const restorable = node.type === 'cluster-region' || (node.type === 'graphNode' && !node.parentId);
      const saved = restorable ? initialPositions[node.id] : undefined;
      return saved ? { ...node, position: saved } : node;
    });
  }, [clusteredLayout, initialNodes, initialPositions]);
  const renderEdges = clusteredLayout?.rfEdges ?? initialEdges;

  // Compute legend data from the graph
  const legendData = useMemo(() => {
    const nodeTypesList: NodeType[] = renderNodes
      .filter((n) => !n.hidden && n.type !== 'cluster-region')
      .map((n) => (n.data as GraphNodeData).nodeType);

    const hasInternalEdges = renderEdges.some(
      (e) => (e.data as ResearchEdgeData)?.isInternal
    );
    const hasHierarchyEdges = renderEdges.some(
      (e) => (e.data as ResearchEdgeData)?.isHierarchy
    );
    const hasConditionalEdges = renderEdges.some(
      (e) => (e.data as ResearchEdgeData)?.isConditional
    );

    return { nodeTypes: nodeTypesList, hasInternalEdges, hasHierarchyEdges, hasConditionalEdges };
  }, [renderNodes, renderEdges]);

  const [nodes, setNodes, onNodesChange] = useNodesState(renderNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(renderEdges);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const lastFittedSignature = useRef<string | null>(null);
  const hasUserMovedNodesRef = useRef(false);
  const fitRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isReactFlowReady, setIsReactFlowReady] = useState(false);
  const [fittedSignature, setFittedSignature] = useState<string | null>(null);
  const [hasUserMovedNodes, setHasUserMovedNodes] = useState(false);

  const graphStructureSignature = useMemo(
    () => JSON.stringify({
      nodes: renderNodes.filter((node) => !node.hidden).map((node) => node.id),
      edges: renderEdges.map((edge) => `${edge.source}->${edge.target}`),
    }),
    [renderNodes, renderEdges],
  );

  const graphSignature = useMemo(
    () => JSON.stringify({
      nodes: renderNodes
        .filter((node) => !node.hidden)
        .map((node) => `${node.id}:${Math.round(node.position.x)}:${Math.round(node.position.y)}`),
      edges: renderEdges.map((edge) => `${edge.source}->${edge.target}`),
    }),
    [renderNodes, renderEdges],
  );

  React.useEffect(() => {
    hasUserMovedNodesRef.current = false;
    setHasUserMovedNodes(false);
  }, [graphStructureSignature]);

  // Sync nodes/edges when graph structure changes (e.g., from workspace operations)
  // Do NOT include selectedNodeId - that causes position reset on selection change
  React.useEffect(() => {
    setNodes(renderNodes);
    setEdges(renderEdges);
  }, [renderNodes, renderEdges, setNodes, setEdges]);

  // Note: openNodeId is provided via OpenNodeIdContext below — no setNodes needed.

  // React Flow's fitView prop only runs on mount. Project/workspace data often
  // arrives after mount, and ELK can finalize positions after the first render.
  React.useEffect(() => {
    if (
      !isReactFlowReady ||
      renderNodes.length === 0 ||
      hasUserMovedNodes ||
      lastFittedSignature.current === graphSignature
    ) {
      return;
    }

    let attempts = 0;

    const fitWhenMeasured = () => {
      const instance = reactFlowInstance.current;
      if (
        !instance ||
        hasUserMovedNodesRef.current ||
        lastFittedSignature.current === graphSignature
      ) {
        return;
      }

      const visibleNodes = instance.getNodes().filter((node) => !node.hidden);
      const nodesMeasured = visibleNodes.length > 0 && visibleNodes.every((node) => node.width && node.height);

      if (nodesMeasured) {
        instance.fitView({
          nodes: visibleNodes.map((node) => ({ id: node.id })),
          padding: 0.3,
          duration: 0,
        });
        lastFittedSignature.current = graphSignature;
        setFittedSignature(graphSignature);
        return;
      }

      attempts += 1;
      if (attempts < 8) {
        fitRetryTimer.current = setTimeout(fitWhenMeasured, 50);
      } else {
        lastFittedSignature.current = graphSignature;
        setFittedSignature(graphSignature);
      }
    };

    fitRetryTimer.current = setTimeout(fitWhenMeasured, 0);

    return () => {
      if (fitRetryTimer.current !== null) {
        clearTimeout(fitRetryTimer.current);
      }
    };
  }, [graphSignature, hasUserMovedNodes, renderNodes.length, isReactFlowReady]);

  const isInitialFitPending = (
    renderNodes.length > 0 &&
    !hasUserMovedNodes &&
    fittedSignature !== graphSignature
  );

  // Update selection state separately without resetting positions
  React.useEffect(() => {
    setNodes((currentNodes) =>
      currentNodes.map((node) => ({
        ...node,
        selected: node.id === selectedNodeId,
      }))
    );
  }, [selectedNodeId, setNodes]);

  // Normalised edge list for cluster highlighting — uses the same key format the
  // rendered research edges use (`${source}-${target}`) so HighlightContext matches.
  const highlightEdges = useMemo<HighlightEdge[]>(() => {
    const edges = (graph?.edges || []) as Array<{ source_node_id: string; target_node_id: string }>;
    return edges.map((e) => ({
      source: e.source_node_id,
      target: e.target_node_id,
      key: `${e.source_node_id}-${e.target_node_id}`,
    }));
  }, [graph]);

  // Member node ids for a cluster region. The unsorted pseudo-cluster has no
  // Cluster record, so derive its members from the node→cluster mapping.
  const clusterMemberIds = useCallback((clusterId: string): string[] => {
    if (clusterId === UNSORTED_CLUSTER_NODE_ID) {
      return Array.from(nodeClusterById.entries())
        .filter(([, cid]) => !cid)
        .map(([nodeId]) => nodeId);
    }
    return clustersForGraph[clusterId]?.member_node_ids ?? [];
  }, [clustersForGraph, nodeClusterById]);

  // Compute path highlight state — consumed by GraphNode and ResearchEdge via context.
  // Using context (like OpenNodeIdContext) avoids the setNodes-overwrite problem where
  // the sync effect resets opacity styles applied by separate effects.
  const highlightState = useMemo<HighlightState>(() => {
    if (selectedClusterId) {
      return clusterHighlight(clusterMemberIds(selectedClusterId), highlightEdges);
    }
    if (!selectedNodeId || !graph?.nodes || !graph?.edges) {
      return EMPTY_HIGHLIGHT;
    }

    // Check if selected node is a conclusion
    let isConclusion = false;
    if (isFlatFormat) {
      const processNodes = graph.nodes as unknown as ProcessNode[];
      const node = processNodes.find(n => n.node_id === selectedNodeId);
      isConclusion = node?.node_type === 'conclusion';
    } else {
      const flatNodes = flattenAllNodes(graph.nodes as LogicalNode[]);
      const node = flatNodes.find(fn => fn.node.node_id === selectedNodeId);
      isConclusion = node?.node.node_type === 'conclusion';
    }

    if (isConclusion) {
      // Conclusions: highlight full path back to root
      const path = computePathToNode(selectedNodeId);
      if (path.nodeIds.size <= 1) return EMPTY_HIGHLIGHT;
      return { nodeIds: path.nodeIds, edgeKeys: path.edgeKeys, active: true, mode: 'node' };
    }

    // Non-conclusions: highlight direct neighbours (inputs one hop back + outputs one hop forward)
    const nodeIds = new Set<string>([selectedNodeId]);
    const edgeKeys = new Set<string>();

    if (isFlatFormat) {
      const processEdges = (graph.edges || []) as unknown as ProcessEdge[];
      processEdges.forEach(e => {
        if (e.target_node_id === selectedNodeId) {
          nodeIds.add(e.source_node_id);
          edgeKeys.add(`${e.source_node_id}-${e.target_node_id}`);
        }
        if (e.source_node_id === selectedNodeId) {
          nodeIds.add(e.target_node_id);
          edgeKeys.add(`${e.source_node_id}-${e.target_node_id}`);
        }
      });
    } else {
      (graph.edges || []).forEach((e: { source_node_id: string; target_node_id: string }) => {
        if (e.target_node_id === selectedNodeId) {
          nodeIds.add(e.source_node_id);
          edgeKeys.add(`${e.source_node_id}-${e.target_node_id}`);
        }
        if (e.source_node_id === selectedNodeId) {
          nodeIds.add(e.target_node_id);
          edgeKeys.add(`${e.source_node_id}-${e.target_node_id}`);
        }
      });
    }

    if (nodeIds.size <= 1) return EMPTY_HIGHLIGHT; // No connected edges at all
    return { nodeIds, edgeKeys, active: true, mode: 'node' };
  }, [selectedNodeId, selectedClusterId, clusterMemberIds, highlightEdges, graph, isFlatFormat, computePathToNode]);

  // Cluster header rectangles (flow coords) for the over-header edge ghost. The
  // body node (type 'cluster-region') is top-level, so its position is already
  // absolute; the header strip occupies the top HEADER_LAYER_HEIGHT of it.
  // Recomputed as clusters are dragged so the ghost tracks live positions.
  const clusterHeaderRects = useMemo<HeaderRect[]>(() => {
    return nodes
      .filter((n) => n.type === 'cluster-region' && !n.hidden)
      .map((n) => ({
        x: n.position.x,
        y: n.position.y,
        width: typeof n.style?.width === 'number' ? n.style.width : (n.width ?? 0),
        height: HEADER_LAYER_HEIGHT,
      }))
      .filter((r) => r.width > 0);
  }, [nodes]);

  // When a node/cluster is selected, lift its connected edges ABOVE the cluster
  // headers (region z=3) so the connections read on top; everything else stays at
  // the resting layer (below the header). We DERIVE the zIndex onto the edges we
  // hand React Flow rather than mutating edge state in an effect: a prior effect
  // approach got clobbered — the structure-sync `setEdges(renderEdges)` (and React
  // Flow's own edge bookkeeping) reset zIndex back to resting on the very re-render
  // the lift triggered, so the elevated svg layer never materialised and the teal
  // edges stayed pinned under the z=3 header. Deriving makes the zIndex a pure
  // function of highlightState — immune to any setEdges race. (ResearchEdge reads
  // the same edgeKeys to colour the path, so colour and layer never disagree.)
  const edgesForFlow = useMemo(() => {
    if (!highlightState.active) return edges;
    return edges.map((e) => {
      const z = highlightState.edgeKeys.has(e.id)
        ? GRAPH_LAYER.edgeElevated
        : GRAPH_LAYER.edgeResting;
      return e.zIndex === z ? e : { ...e, zIndex: z };
    });
  }, [edges, highlightState]);

  // Handle highlight effect when a reference is clicked in chat
  // Applies pulse animation and centers view on the highlighted node
  React.useEffect(() => {
    if (!highlightedNodeId) return;

    // Apply highlight styling to the target node
    setNodes((currentNodes) =>
      currentNodes.map((node) => ({
        ...node,
        className: node.id === highlightedNodeId
          ? 'highlighted-node'
          : node.className?.replace('highlighted-node', '').trim() || undefined,
      }))
    );

    // Center view on the highlighted node after a brief delay for animation
    if (reactFlowInstance.current) {
      setTimeout(() => {
        reactFlowInstance.current?.fitView({
          nodes: [{ id: highlightedNodeId }],
          padding: 0.5,
          duration: 500,
        });
      }, 100);
    }
  }, [highlightedNodeId, setNodes]);

  // Handle node selection and position changes
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);

      // Track selection changes - collect all selection events first
      // to properly handle clicking between nodes (deselect old + select new)
      const selectionChanges = changes.filter(
        (change): change is NodeChange & { type: 'select'; selected: boolean } =>
          change.type === 'select' && change.selected !== undefined
      );

      if (selectionChanges.length > 0) {
        // Find which node (if any) is now selected
        const newlySelected = selectionChanges.find((c) => c.selected);
        const newSelectedId = newlySelected ? newlySelected.id : null;

        setSelectedNodeId(newSelectedId);
        onNodeSelect?.(newSelectedId);
        // Selecting a node clears any active cluster highlight.
        if (newSelectedId) {
          setSelectedClusterId(null);
        }
      }

      // Track position changes (when drag ends)
      const hasPositionChanges = changes.some(
        (change) => change.type === 'position' && change.dragging === false
      );

      if (hasPositionChanges && editable && onPositionsChange) {
        hasUserMovedNodesRef.current = true;
        setHasUserMovedNodes(true);

        // Extract current positions from nodes after React Flow updates
        // We need to defer this to get the updated positions
        setTimeout(() => {
          setNodes((currentNodes) => {
            const positions: Record<string, { x: number; y: number }> = {};
            currentNodes.forEach((node) => {
              positions[node.id] = { x: node.position.x, y: node.position.y };
            });
            onPositionsChange(positions);
            return currentNodes;
          });
        }, 0);
      }
    },
    [onNodesChange, onNodeSelect, editable, onPositionsChange, setNodes]
  );

  // Handle click on empty canvas to deselect
  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedClusterId(null);
    onNodeSelect?.(null);
  }, [onNodeSelect]);

  // Cluster regions are not React Flow-selectable (so they don't steal node
  // selection / the findings panel), so capture their clicks here. Clicking a
  // region toggles its highlight; clicking a non-region node falls through to
  // the native selection path in handleNodesChange.
  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (node.type !== 'cluster-region') return;
      setSelectedClusterId((prev) => (prev === node.id ? null : node.id));
      setSelectedNodeId(null);
      onNodeSelect?.(null);
    },
    [onNodeSelect]
  );

  // Handle re-layout button click
  const handleReLayout = useCallback(() => {
    if (!graph || !graph.nodes || graph.nodes.length === 0) return;
    if (!enableClusters) {
      if (isFlatFormat) {
        const processNodes = graph.nodes as unknown as ProcessNode[];
        const processEdges = (graph.edges || []) as unknown as ProcessEdge[];
        calculateElkLayoutFlat(processNodes, processEdges).then((newLayout) => {
          setNodes((currentNodes) =>
            currentNodes.map((node) => ({
              ...node,
              position: newLayout.positions[node.id] || node.position,
            }))
          );
        });
      } else {
        calculateElkLayout(graph.nodes, graph.edges).then((newLayout) => {
          setNodes((currentNodes) =>
            currentNodes.map((node) => ({
              ...node,
              position: newLayout.positions[node.id] || node.position,
            }))
          );
        });
      }
      return;
    }
    hasUserMovedNodesRef.current = false;
    setHasUserMovedNodes(false);
    // Discard saved node_positions so the fresh clustered layout actually
    // renders. Otherwise renderNodes restores stale/dragged cluster-region
    // positions on top of the new layout, which de-syncs them from the
    // freshly-centered unclustered source (QUESTION) and the tier layout.
    // Clearing makes Re-layout a true "reset to the clean auto-layout".
    onPositionsChange?.({});
    setClusterLayoutVersion((version) => version + 1);
  }, [enableClusters, graph, isFlatFormat, setNodes, onPositionsChange]);

  // Auto re-layout when triggerReLayout prop changes (e.g., on research completion)
  useEffect(() => {
    if (triggerReLayout && triggerReLayout > 0) {
      // Small delay to ensure graph is fully rendered before re-layout
      const timer = setTimeout(() => {
        handleReLayout();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [triggerReLayout, handleReLayout]);

  // Empty state
  if (!graph || !graph.nodes || graph.nodes.length === 0) {
    return (
      <GraphCanvas
        title={graphTitle}
        subtitle={graphSubtitle}
        legend={statusLegend}
        showEdgeLabels={showEdgeLabels}
        onToggleEdgeLabels={setShowEdgeLabels}
      >
        <div className="w-full h-full flex items-center justify-center bg-bg">
          <div className="text-center">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-fg-tertiary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
              />
            </svg>
            <p className="text-lg font-semibold text-fg">No evidence graph</p>
            <p className="text-sm mt-1 text-fg-secondary">Add evidence to start building your research graph</p>
          </div>
        </div>
      </GraphCanvas>
    );
  }

  return (
    <GraphCanvas
      title={graphTitle}
      subtitle={graphSubtitle}
      legend={statusLegend}
      showEdgeLabels={showEdgeLabels}
      onToggleEdgeLabels={setShowEdgeLabels}
    >
    <OpenNodeIdContext.Provider value={openNodeId ?? null}>
    <HighlightContext.Provider value={highlightState}>
    <ClusterHeaderRectsContext.Provider value={clusterHeaderRects}>
    <SelectedClusterContext.Provider value={selectedClusterId}>
    <HoverContext.Provider value={hoveredNodeId}>
    <div className="w-full h-full relative">
      {/* Highlight animation styles for reference clicks — teal pulse derived
          from --secondary so it tracks the typed brand color rather than
          freezing teal as a literal RGBA. Each declaration is paired with a
          static rgba(13, 148, 136, X) fallback so older browsers without
          color-mix support (Safari <16.4, locked-down banking environments)
          still see the pulse — they just stop tracking --secondary if it's
          ever retuned. NOTE: if --secondary ever changes value, the rgba
          fallback values below must be updated by hand (they hardcode
          #0d9488 = rgb(13, 148, 136)). This is an intentional duplication
          for browser-support resilience. */}
      <style>{`
        .react-flow__node.highlighted-node {
          animation: highlight-pulse 1.5s ease-in-out;
          box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.5);
          box-shadow: 0 0 0 4px color-mix(in srgb, var(--secondary) 50%, transparent);
          border-radius: 8px;
        }
        @keyframes highlight-pulse {
          0%   { box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.8);  box-shadow: 0 0 0 4px color-mix(in srgb, var(--secondary) 80%, transparent); }
          50%  { box-shadow: 0 0 0 8px rgba(13, 148, 136, 0.35); box-shadow: 0 0 0 8px color-mix(in srgb, var(--secondary) 35%, transparent); }
          100% { box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.5);  box-shadow: 0 0 0 4px color-mix(in srgb, var(--secondary) 50%, transparent); }
        }
        /* Calmer React Flow controls — minimap + zoom stack. */
        .react-flow__controls {
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          background: var(--surface);
          overflow: hidden;
        }
        .react-flow__controls-button {
          background: var(--surface);
          border-bottom: 1px solid var(--divider);
          color: var(--fg-secondary);
        }
        .react-flow__controls-button:hover {
          background: var(--bg-warm);
          color: var(--fg);
        }
        .react-flow__controls-button svg {
          fill: currentColor;
        }
        .react-flow__minimap {
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          background: var(--surface);
          overflow: hidden;
        }
        .react-flow__attribution {
          background: transparent;
          color: var(--fg-tertiary);
          font-size: 10px;
        }
      `}</style>

      {/* Re-layout button — above minimap (bottom-right) */}
      <button
        onClick={handleReLayout}
        className="absolute bottom-44 right-2 z-10 px-3 py-1.5 bg-surface border border-border-default rounded-md shadow-bark-sm hover:bg-bg-warm hover:border-accent hover:text-accent-hover text-sm font-medium text-fg-secondary flex items-center gap-2 transition-colors"
        title="Organize nodes in hierarchical flow"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" />
        </svg>
        Re-layout
      </button>

      {isInitialFitPending && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-bg-warm text-sm text-fg-tertiary">
          Loading graph...
        </div>
      )}

      <ReactFlow
        className={isInitialFitPending ? 'opacity-0' : undefined}
        nodes={nodes}
        edges={edgesForFlow}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onPaneClick={handlePaneClick}
        onNodeClick={handleNodeClick}
        onNodeMouseEnter={(_event, node) => setHoveredNodeId(node.id)}
        onNodeMouseLeave={() => setHoveredNodeId(null)}
        onNodeDoubleClick={onNodeDoubleClick ? (_event, node) => {
          if (node.type !== 'cluster-region') {
            onNodeDoubleClick(node.id);
          }
        } : undefined}
        onInit={(instance) => {
          reactFlowInstance.current = instance;
          setIsReactFlowReady(true);
        }}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={editable}
        nodesConnectable={editable}
        elementsSelectable={true}
        minZoom={0.3}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
      >
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(node) => {
            if (node.type === 'cluster-region') {
              return 'var(--border-default)';
            }
            const data = node.data as WorkspaceNodeData;
            return getNodeTypeColor(data.nodeType);
          }}
          nodeStrokeWidth={2}
          maskColor="rgba(248, 250, 251, 0.6)"
        />
        {/* Background dot grid uses --divider so nodes read clearly on top. */}
        <Background variant={BackgroundVariant.Dots} gap={20} size={1.5} color="var(--divider)" />
      </ReactFlow>

      {/* Legend panel */}
      <GraphLegend
        nodeTypes={legendData.nodeTypes}
        hasInternalEdges={legendData.hasInternalEdges}
        hasHierarchyEdges={legendData.hasHierarchyEdges}
        hasConditionalEdges={legendData.hasConditionalEdges}
      />
    </div>
    </HoverContext.Provider>
    </SelectedClusterContext.Provider>
    </ClusterHeaderRectsContext.Provider>
    </HighlightContext.Provider>
    </OpenNodeIdContext.Provider>
    </GraphCanvas>
  );
}
